import { useState, useEffect } from 'react';
import { Modal, ModalFooter } from './Modal';
import { Input } from './FormField';
import { Button } from '../ui/Button';
import { ErrorDisplay } from '../ui/ErrorDisplay';
import { ColorPicker } from './ColorPicker';
import { IconPicker, getIconComponent } from './IconPicker';
import { productApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface Product {
  id: string;
  name: string;
  icon: string;
  iconColor: string;
  displayOrder: number;
  _count?: {
    subProducts: number;
  };
}

interface ProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  product?: Product | null;
  onSuccess: () => void;
}

export function ProductModal({
  isOpen,
  onClose,
  product,
  onSuccess,
}: ProductModalProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    icon: 'briefcase',
    iconColor: '#3B82F6',
    displayOrder: 0,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<Error | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditMode = Boolean(product);

  // Populate form when editing
  useEffect(() => {
    if (product) {
      setFormData({
        name: product.name || '',
        icon: product.icon || 'briefcase',
        iconColor: product.iconColor || '#3B82F6',
        displayOrder: product.displayOrder || 0,
      });
    } else {
      setFormData({
        name: '',
        icon: 'briefcase',
        iconColor: '#3B82F6',
        displayOrder: 0,
      });
    }
    setErrors({});
    setSubmitError(null);
  }, [product, isOpen]);

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (formData.name.trim().length > 100) {
      newErrors.name = 'Name must be 100 characters or less';
    }

    if (!formData.icon) {
      newErrors.icon = 'Icon is required';
    }

    if (!formData.iconColor || !/^#[0-9A-F]{6}$/i.test(formData.iconColor)) {
      newErrors.iconColor = 'Valid color is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const data = {
        name: formData.name.trim(),
        icon: formData.icon,
        iconColor: formData.iconColor,
        displayOrder: formData.displayOrder,
        createdBy: user?.id,
      };

      if (isEditMode && product) {
        await productApi.update(product.id, data);
      } else {
        await productApi.create(data);
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to save product:', error);
      setSubmitError(error instanceof Error ? error : new Error('Failed to save product'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!product) return;

    if (!confirm(`Are you sure you want to delete "${product.name}"?`)) {
      return;
    }

    setIsSubmitting(true);

    try {
      await productApi.delete(product.id);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Failed to delete product:', error);
      setSubmitError(error instanceof Error ? error : new Error('Failed to delete product'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the icon component for preview
  const IconComponent = getIconComponent(formData.icon);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit product' : 'Add new product'}
      size="medium"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Name"
          name="name"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="Payments"
          required
          error={errors.name}
        />

        <IconPicker
          label="Icon"
          value={formData.icon}
          onChange={(icon) => setFormData({ ...formData, icon })}
          required
          error={errors.icon}
        />

        <ColorPicker
          label="Icon color"
          value={formData.iconColor}
          onChange={(color) => setFormData({ ...formData, iconColor: color })}
          required
          error={errors.iconColor}
        />

        <Input
          label="Display order"
          name="displayOrder"
          type="number"
          value={formData.displayOrder}
          onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })}
          min={0}
          helpText="Controls the order in which products appear in lists"
        />

        {isEditMode && product?._count && (
          <div className="rounded-md bg-blue-50 border border-blue-200 p-3">
            <p className="text-sm text-blue-800">
              This product has <strong>{product._count.subProducts}</strong> sub-product
              {product._count.subProducts !== 1 ? 's' : ''}
            </p>
          </div>
        )}

        <div className="rounded-md bg-surface-subtle border border-border-subtle p-3">
          <div className="text-sm font-medium text-text-primary mb-2">
            Product preview
          </div>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: formData.iconColor + '20' }}
            >
              {IconComponent && (
                <div style={{ color: formData.iconColor }}>
                  <IconComponent className="h-6 w-6" />
                </div>
              )}
            </div>
            <span className="text-sm font-medium text-text-primary">
              {formData.name || 'Preview'}
            </span>
          </div>
        </div>

        <ErrorDisplay error={submitError} />

        <ModalFooter>
          {isEditMode && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              disabled={isSubmitting}
              className="mr-auto"
            >
              Delete
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditMode ? 'Save changes' : 'Add product'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
